import React, { useState, useEffect, useCallback, useRef } from 'react';
import BigNumber from 'bignumber.js';
import { Copy, CheckCheck } from 'lucide-react';
import { isValidNumber } from '../../utils/numberFormatting';
import ValidationError from '../../errors/components/ValidationError';
import NumericSpinnerButtons from './NumericSpinnerButtons';
import { useCopy } from '../../contexts/CopyContext';

const NumberInput = ({
  value,
  onChange,
  prefix,
  suffix,
  decimals = 8,
  min,
  max,
  step,
  required = false,
  error,
  name,
  disabled = false,
  highlight = false,
  autoFocus = false,
  showSpinners = true,
  copyEnabled = false,
  formatOptions = {}
}) => {
  // Track both displayed value and whether user has modified it
  const [displayValue, setDisplayValue] = useState('');
  const [hasUserModified, setHasUserModified] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Track precision of the input value — initialize from decimals prop
  // so that small values like 0.003 aren't truncated on first render
  const [precision, setPrecision] = useState(() => {
    // If value already has more decimal places than default 2, use that
    if (value instanceof BigNumber && value.isFinite() && value.dp() > 2) {
      return Math.min(value.dp(), decimals);
    }
    return 2;
  });

  // Track if we should force display with full precision (for arrow key navigation)
  const [forceFullPrecision, setForceFullPrecision] = useState(false);

  const inputRef = useRef(null);

  const { copiedField, handleCopy } = useCopy();

  // Format a number for display based on focus state and precision
  const formatForDisplay = useCallback((val, focused = false) => {
    if (!val && val !== 0) return '';

    try {
      const bn = new BigNumber(val);
      if (bn.isNaN()) return '';

      // When focused, show unformatted number
      if (focused) return bn.decimalPlaces(decimals).toString();

      // If forceIntegerDisplay option is enabled, always display integers without decimal fractions
      if (formatOptions.forceIntegerDisplay) {
        return bn.integerValue(BigNumber.ROUND_HALF_UP).toString();
      }

      // If we need to force full precision (after arrow keys), use current precision
      if (forceFullPrecision) {
        return bn.toFixed(precision);
      }

      // For display, format using precision but remove trailing zeros
      let strValue = bn.toFixed(precision);

      // Remove trailing zeros after the decimal point
      if (strValue.includes('.')) {
        // Remove trailing zeros
        while (strValue.endsWith('0')) {
          strValue = strValue.slice(0, -1);
        }

        // If we've removed everything after the decimal point, remove the decimal point too
        if (strValue.endsWith('.')) {
          strValue = strValue.slice(0, -1);
        }
      }

      return strValue;
    } catch (error) {
      return '';
    }
  }, [precision, decimals, forceFullPrecision, formatOptions]);

  // Initialize and update display value from props
  useEffect(() => {
    if (!hasUserModified || !isFocused) {
      setDisplayValue(formatForDisplay(value, isFocused));
    }
  }, [value, isFocused, hasUserModified, formatForDisplay]);

  // Clear local error when value is valid (auto-corrected)
  useEffect(() => {
    if (value && min && !value.isLessThan(min)) {
      setLocalError(null);
    }
  }, [value, min]);

  // Get step value for increment/decrement based on precision
  const getStepValue = useCallback(() => {
    // If step is defined, use it with proper precision
    if (step !== undefined) {
      const stepBN = new BigNumber(step);
      return stepBN.dp(precision);
    }

    // Calculate dynamic step with current precision
    const dynamicStep = new BigNumber(10).pow(-precision);
    return dynamicStep.dp(precision);
  }, [step, precision]);

  // Handle increment button click
  const handleIncrement = useCallback((multiplier = 1) => {
    if (disabled) return;

    const currentValue = value || new BigNumber(0);
    const stepValue = getStepValue().times(multiplier);

    let newValue = currentValue.plus(stepValue);

    // Apply max constraint
    if (max !== undefined) {
      const maxValue = new BigNumber(max);
      if (newValue.isGreaterThan(maxValue)) {
        newValue = maxValue;
      }
    }

    // For fields with forceIntegerDisplay, no need to enable decimal fraction formatting
    if (!formatOptions.forceIntegerDisplay) {
      setForceFullPrecision(true);
    }

    setHasUserModified(false);

    onChange(newValue);
  }, [value, disabled, getStepValue, max, onChange, formatOptions]);

  // Handle decrement button click
  const handleDecrement = useCallback((multiplier = 1) => {
    if (disabled) return;

    const currentValue = value || new BigNumber(0);
    const stepValue = getStepValue().times(multiplier);

    let newValue = currentValue.minus(stepValue);

    // Prevent negative values if min not specified
    if (min === undefined && newValue.isNegative()) {
      newValue = new BigNumber(0);
    }

    // Apply min constraint
    if (min !== undefined) {
      const minValue = new BigNumber(min);
      if (newValue.isLessThan(minValue)) {
        newValue = minValue;
      }
    }

    // For fields with forceIntegerDisplay, no need to enable decimal fraction formatting
    if (!formatOptions.forceIntegerDisplay) {
      setForceFullPrecision(true);
    }

    setHasUserModified(false);

    onChange(newValue);
  }, [value, disabled, getStepValue, min, onChange, formatOptions]);

  // Handle changes to the input field
  const handleChange = (e) => {
    // Mark that user has made changes
    setHasUserModified(true);

    // Reset full precision mode when user manually edits
    setForceFullPrecision(false);

    // Get the input value and replace comma with dot
    let inputValue = e.target.value.replace(/,/g, '.');

    // Ensure only one decimal point
    const parts = inputValue.split('.');
    if (parts.length > 2) {
      inputValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Remove any non-numeric characters except dot
    inputValue = inputValue
      .replace(/[^0-9.]/g, '');

    // Truncate excess decimal places
    if (inputValue.includes('.')) {
      const [intPart, decPart] = inputValue.split('.');
      if (decPart && decPart.length > decimals) {
        inputValue = intPart + '.' + decPart.slice(0, decimals);
      }
    }

    // Save the filtered value to display
    setDisplayValue(inputValue);

    // Update precision based on user input - count digits after decimal point
    if (inputValue.includes('.')) {
      const decimalPart = inputValue.split('.')[1] || '';
      if (decimalPart.length > 0) {
        setPrecision(Math.min(decimalPart.length, decimals));
      }
    }

    // Only update parent if we have a valid number
    if (inputValue && isValidNumber(inputValue)) {
      const numValue = new BigNumber(inputValue);

      // Special case: Allow entering decimal values like "0." or "0.0" without triggering min validation
      if (inputValue === '0' || inputValue === '0.' || /^0\.\d*$/.test(inputValue)) {
        const isPartialDecimalInput = inputValue === '0' || inputValue === '0.' || /^0\.0*$/.test(inputValue);

        if (!isPartialDecimalInput && min !== undefined && numValue.isLessThan(min)) {
          setLocalError({ message: `Value must be at least ${min}` });
          return;
        }
      } else if (min !== undefined && numValue.isLessThan(min)) {
        setLocalError({ message: `Value must be at least ${min}` });
        return;
      }

      if (max !== undefined && numValue.isGreaterThan(max)) {
        setLocalError({ message: `Value must be less than ${max}` });
        return;
      }

      setLocalError(null);
      onChange(numValue);
    } else if (inputValue === '' || inputValue === '.') {
      // Allow temporary incomplete states
      setLocalError(null);
    } else {
      setLocalError({ message: 'Invalid number' });
    }
  };

  // Handle focus events
  const handleFocus = () => {
    setIsFocused(true);
    // When focusing, show clean number without formatting
    setDisplayValue(value ? value.decimalPlaces(decimals).toString() : '');
    setHasUserModified(false); // Reset the flag when focusing
  };

  // Handle blur events
  const handleBlur = () => {
    setIsFocused(false);
    setHasUserModified(false);

    // If input is empty but not required, restore the original value
    if (!displayValue && value) {
      setDisplayValue(formatForDisplay(value));
      return;
    }

    // If input has valid content, format it properly
    if (displayValue && isValidNumber(displayValue)) {
      const numValue = new BigNumber(displayValue);

      // Check if value meets minimum requirement
      if (min !== undefined && numValue.isLessThan(min)) {
        setLocalError({ message: `Value must be at least ${min}` });
        // Auto-correct to minimum value
        const correctedValue = new BigNumber(min);
        setDisplayValue(formatForDisplay(correctedValue));
        onChange(correctedValue);
        return;
      } else {
        // Clear error if value is valid
        setLocalError(null);
      }

      // If we're not in forced precision mode, reset it when blurring
      if (!forceFullPrecision) {
        setDisplayValue(formatForDisplay(numValue));
      }

      // Only update parent if value actually changed
      if (!value || !numValue.isEqualTo(value)) {
        onChange(numValue);
      }
    } else if (required && (!displayValue || !isValidNumber(displayValue))) {
      setLocalError({ message: 'This field is required' });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    // Prevent input of non-numeric characters except specific keys
    if (!/^[0-9.,]$/.test(e.key) && // Allow only numbers, comma, and dot
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Escape'].includes(e.key) && // Allow navigation keys
      !(e.ctrlKey || e.metaKey)) { // Allow copy/paste shortcuts
      e.preventDefault();
      return;
    }

    // Reset full precision mode when typing non-arrow keys
    if (!['ArrowUp', 'ArrowDown'].includes(e.key)) {
      setForceFullPrecision(false);
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault(); // Prevent page scrolling

      // Apply multiplier based on keyboard modifiers
      let multiplier = 1;

      if (e.shiftKey && e.ctrlKey) {
        multiplier = 100;
      } else if (e.ctrlKey) {
        multiplier = 10;
      } else if (e.shiftKey) {
        multiplier = 5;
      }

      // Call appropriate handler based on arrow key
      if (e.key === 'ArrowUp') {
        handleIncrement(multiplier);
      } else {
        handleDecrement(multiplier);
      }
    } else if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Calculate base value for display
  let baseDisplayValue = isFocused
    ? (forceFullPrecision && value instanceof BigNumber && value.isFinite()
      ? value.toFixed(precision)
      : displayValue)
    : (value instanceof BigNumber && value.isFinite()
      ? formatForDisplay(value, false)
      : '');

  // Add prefix/suffix only when field is not focused
  const finalDisplayValue = baseDisplayValue
    ? (prefix && !isFocused) ? `${prefix}${baseDisplayValue}` :
      (suffix && !isFocused) ? `${baseDisplayValue}${suffix}` :
        baseDisplayValue
    : '';

  const displayError = error || localError;
  const isInvalid = !!displayError;

  const handleCopyClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (!value) return;

    const copyToClipboard = async (text) => {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (error) {
          console.error('Error copying text: ', error);
        }
        textArea.remove();
      }
    };

    const cleanValue = value.toString();
    copyToClipboard(cleanValue)
      .then(() => {
        // Calculate tooltip position based on element position
        if (event && event.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const elementCenterX = rect.left + rect.width / 2;

          let position = 'center';
          if (elementCenterX > viewportWidth - 70) {
            position = 'right';
          }
          else if (elementCenterX < 70) {
            position = 'left';
          }
          handleCopy(name, position);
        } else {
          handleCopy(name);
        }
      })
      .catch(error => {
        console.error('Failed to copy: ', error);
      });
  };

  return (
    <div className="space-y-1 relative">
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={finalDisplayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onMouseDown={(e) => {
            // On the first click, place cursor at the end
            if (!isFocused) {
              e.preventDefault();
              e.target.focus();
              const length = finalDisplayValue.length;
              e.target.setSelectionRange(length, length);
            }
            // When already focused, allow normal text selection behavior
          }}
          name={name}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`
            w-full h-10 border rounded-lg px-3 text-slate-300/90 
            outline-none font-sans
            ${(showSpinners && isFocused) || (copyEnabled && !isFocused) ? 'pr-12' : ''} 
            ${highlight ? 'bg-amber-500/[0.07] border-amber-500/30' :
              isInvalid ? 'bg-slate-800/50 border-crimson-500/50' :
                'bg-slate-800/40 border-white/10 transition-all duration-200 focus:scale-[1.04] focus:border-amber-500/30 focus:shadow-[0_0_0_1px_rgba(255,165,0,0.5)]'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
        {showSpinners && !disabled && isFocused && (
          <div className="absolute right-3">
            <NumericSpinnerButtons
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              disabled={disabled}
              inputRef={inputRef}
            />
          </div>
        )}
        {copyEnabled && !disabled && !isFocused && (
          <div
            className="absolute right-0 top-0 bottom-0 w-[38px] group cursor-pointer hover:bg-white/[0.02] rounded-r-lg transition-colors"
            onClick={handleCopyClick}
            aria-label={`Copy ${name}`}
          >
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {copiedField === name ? (
                <span className="relative inline-flex items-center">
                  <CheckCheck size={14} className="text-emerald-500" />
                  <div className="absolute bottom-full mb-2 text-emerald-500 text-xs bg-emerald-950 px-1.5 py-0.5 rounded select-none pointer-events-none whitespace-nowrap font-normal" style={{
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}>
                    Copied
                    <div className="absolute top-full border-l-4 border-r-4 border-t-4 border-b-0 border-l-transparent border-r-transparent border-emerald-950" style={{
                      height: '4px',
                      width: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }}></div>
                  </div>
                </span>
              ) : (
                <span className="relative inline-flex items-center">
                  <Copy size={14} className="text-slate-500 group-hover:text-slate-300 transition-all" />
                  <div className="absolute bottom-full mb-2 hidden group-hover:block text-slate-300 text-xs bg-slate-950 px-1.5 py-0.5 rounded select-none pointer-events-none whitespace-nowrap font-normal" style={{
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}>
                    Copy value
                    <div className="absolute top-full border-l-4 border-r-4 border-t-4 border-b-0 border-l-transparent border-r-transparent border-slate-950" style={{
                      height: '4px',
                      width: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }}></div>
                  </div>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        {displayError && <ValidationError error={displayError} />}
      </div>
    </div>
  );
};

export default NumberInput;